/**
 * W4D_SmoothScroll
 *
 * Implements a momentum-based smooth scrolling experience using cubic-eased
 * linear interpolation (lerp) on mouse wheel input.
 * Fully adheres to WCAG accessibility guidelines by honoring `prefers-reduced-motion`,
 * uses `AbortController` for clean listener lifecycle management, and supports native `scrollend`.
 */
class W4D_SmoothScroll {
	/** @type {AbortController} Signal controller for tearing down event listeners */
	#controller = new AbortController();

	/** @type {MediaQueryList} Query matcher for user reduced motion preference */
	#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

	/**
	 * @param {Object} [config={}] Configuration overrides
	 * @param {number} [config.lerpFactor=0.05] Base interpolation factor
	 * @param {number} [config.scrollMultiplier=0.65] Multiplier applied to wheel delta
	 * @param {number} [config.threshold=0.5] Pixel distance threshold to snap and halt animation
	 */
	constructor(config = {}) {
		this.config = {
			lerpFactor: 0.05,
			scrollMultiplier: 0.65,
			threshold: 0.5,
			...config,
		};

		// Respect user motion preference (WCAG 2.2 / 2.3.3)
		if (this.#reducedMotionQuery.matches) return;

		// Read lazily on the first wheel/scrollend event: window.scrollY here forced a layout flush at load.
		this.scrollCurrent = null;
		this.scrollTarget = null;
		this.isScrolling = false;
		this.animationFrameId = null;

		this.init();
	}

	/**
	 * Attach event listeners with AbortSignal binding
	 */
	init() {
		const { signal } = this.#controller;

		// Listen for live reduced-motion preference changes
		this.#reducedMotionQuery.addEventListener(
			'change',
			(e) => {
				if (e.matches) this.destroy();
			},
			{ signal }
		);

		// Synchronize scroll positions when scrolling finishes natively (e.g. key press, scrollbar drag)
		const handleScrollEnd = () => {
			if (!this.isScrolling) {
				this.scrollCurrent = window.scrollY;
				this.scrollTarget = window.scrollY;
			}
		};

		// Modern scrollend event with fallback for older browsers
		if ('onscrollend' in window) {
			window.addEventListener('scrollend', handleScrollEnd, { signal, passive: true });
		} else {
			let timeout;
			window.addEventListener(
				'scroll',
				() => {
					clearTimeout(timeout);
					timeout = setTimeout(handleScrollEnd, 100);
				},
				{ signal, passive: true }
			);
		}

		// Intercept wheel events on window
		window.addEventListener('wheel', (e) => this.handleWheel(e), { signal, passive: false });
	}

	/**
	 * Process wheel events, calculate target offsets, and start lerp loop
	 *
	 * @param {WheelEvent} e
	 */
	handleWheel(e) {
		// Ignore modified wheel events (zoom/horizontal) or zero delta
		if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.deltaY === 0) return;

		// Allow nested scrollable elements to handle their own scrolling
		if (this.isInsideScrollable(e.target, e.deltaY)) return;

		e.preventDefault();
		if (this.scrollCurrent === null) {
			this.scrollCurrent = window.scrollY;
			this.scrollTarget = this.scrollCurrent;
		}
		this.scrollTarget += e.deltaY * this.config.scrollMultiplier;

		// Clamp target within valid document scroll bounds
		const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
		this.scrollTarget = Math.max(0, Math.min(this.scrollTarget, maxScroll));

		if (!this.isScrolling) {
			this.isScrolling = true;
			this.animate();
		}
	}

	/**
	 * Traverse ancestor tree to check if event occurred inside an actively scrollable container
	 *
	 * @param {EventTarget} target Event target element
	 * @param {number} deltaY Vertical wheel delta
	 * @returns {boolean} True if inside a scrollable container that can scroll in deltaY direction
	 */
	isInsideScrollable(target, deltaY) {
		let node = target;
		while (node && node !== document.body && node !== document.documentElement) {
			if (node.nodeType === 1) {
				const style = window.getComputedStyle(node);
				const overflowY = style.overflowY;
				if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
					const canScrollUp = node.scrollTop > 0;
					const canScrollDown = Math.ceil(node.scrollTop + node.clientHeight) < node.scrollHeight;
					if ((deltaY < 0 && canScrollUp) || (deltaY > 0 && canScrollDown)) return true;
				}
			}
			node = node.parentElement;
		}
		return false;
	}

	/**
	 * Animation frame loop applying cubic-eased linear interpolation
	 */
	animate() {
		const diff = this.scrollTarget - this.scrollCurrent;

		// Snap to target and stop loop if remaining distance is under threshold
		if (Math.abs(diff) < this.config.threshold) {
			this.scrollCurrent = this.scrollTarget;
			window.scrollTo(0, this.scrollCurrent);
			this.isScrolling = false;
			return;
		}

		// Cubic ease-out calculation for smooth deceleration
		const t = this.config.lerpFactor;
		const easedT = 1 - Math.pow(1 - t, 3);
		this.scrollCurrent += diff * easedT;
		window.scrollTo(0, this.scrollCurrent);

		this.animationFrameId = requestAnimationFrame(() => this.animate());
	}

	/**
	 * Clean up all listeners and halt active animation frame
	 */
	destroy() {
		this.#controller.abort();
		if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
		this.isScrolling = false;
	}
}

// Initialize on DOM ready without jQuery dependency
document.addEventListener('DOMContentLoaded', () => {
	new W4D_SmoothScroll();
});
